import { Modal, ModalBody, ModalHeader, Text } from '@components-deprecated'

import { Color, Icon, Space } from '@billy/design-system'
import { css } from '@emotion/core'
import React, { MouseEvent, useCallback, useState } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { getBankLineDescription } from '@features/bankTransactions/utils/getBankLineDescription'

import { useHover } from '../../../hooks'
import { SpecificState } from '../../../types/reduxSaga-deprecated'
import { reactClass } from '../../../utils'
import { ungroupBankLine } from '../action-creators'
import { BankLine as BankLineType, ReceiptState } from '../types'
import { prefixAmount } from '../utils'
import { LineWrapper } from './LineWrapper'
import { MatchListRowActionOverlay as ActionOverlay } from './MatchListRowActionOverlay'
import * as Styled from './styles'

export type BankLineProps = {
  bankLine: BankLineType
  index: number
  isDraggingOver?: boolean
  isApproved: boolean
  withUngroup: boolean
  collection: BankLineType[]
}

export const BankLine = ({
  bankLine: { amount = 0, comments, description, entryDate = '', id, matchId, receiptState, side },
  index,
  isApproved,
  withUngroup,
  ...rest
}: BankLineProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isUngrouping, setIsUngrouping] = useState(false)
  const account = useSelector((state: SpecificState) => state.app.account)
  const { hoverRef, isHovered } = useHover()
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const isCommentsIconVisible = receiptState !== ReceiptState.MissingReceipt && !!comments?.length

  const handleUngroup = () => {
    if (isUngrouping) {
      return
    }
    setIsUngrouping(true)
    dispatch(
      ungroupBankLine(
        {
          accountId: account?.id as string,
          amount,
          entryDate,
          isApproved: false,
          lineId: id,
          matchId,
          side,
        },
        (err) => {
          if (err) {
            setIsUngrouping(false)
          }
        },
      ),
    )
  }

  const handleModalOpen = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    setIsCommentModalOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setIsCommentModalOpen(false)
  }, [])

  return (
    <>
      <Draggable draggableId={id} index={index} isDragDisabled={isApproved}>
        {(provided, snapshot) => (
          <LineWrapper
            className={reactClass('bankline')}
            groupable
            isDragging={snapshot.isDragging}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...provided.draggableProps.style,
              transform: snapshot.isDragging ? provided.draggableProps.style?.transform : 'unset',
            }}
            {...rest}
          >
            <div ref={hoverRef} style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}>
              <ActionOverlay
                className="ungroup-line"
                hoverable={isHovered && withUngroup}
                css={css`
                  top: 0;
                  left: 0;
                `}
              >
                <Icon icon="x" onClick={handleUngroup} />
              </ActionOverlay>
            </div>

            <span style={{ width: '90px' }}>
              <Text type="date">{entryDate}</Text>
            </span>
            <Space horizontal />
            <div
              css={css`
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                word-break: keep-all;
                white-space: nowrap;
              `}
            >
              {isCommentsIconVisible && (
                <Styled.CommentButton type="button" onClick={handleModalOpen}>
                  <Icon icon="circleQuestionMark" color={Color.Lavender500} />
                </Styled.CommentButton>
              )}
              {receiptState === ReceiptState.MissingReceipt && (
                <Styled.HelpBadge>
                  <Icon icon="circleQuestionMark" name={t('things_to_do.missing_receipts')} />
                </Styled.HelpBadge>
              )}
              <Text truncate>{getBankLineDescription(description)}</Text>
            </div>
            <Text
              type="currency"
              currencyColored
              css={css`
                min-width: 60px;
                text-align: right;
              `}
            >
              {prefixAmount({ amount, side })}
            </Text>
          </LineWrapper>
        )}
      </Draggable>
      {isCommentsIconVisible && (
        <Modal isOpen={isCommentModalOpen} onClose={handleModalClose}>
          <ModalHeader heading="Comments" />
          <ModalBody>
            {comments?.map(({ message }) => (
              <Text>{message}</Text>
            ))}
          </ModalBody>
        </Modal>
      )}
    </>
  )
}
